/**
 * Onboarding funnel analytics hook.
 * Consumes get_onboarding_funnel_analytics(p_days).
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export interface OnboardingProfileStep {
    step: string;
    opened: number;
    completed: number;
}

export interface OnboardingPermissionResult {
    attempt?: number;
    result: string;
    n: number;
}

export interface OnboardingDeeplinkRow {
    source: string;
    target: string;
    opens: number;
    unique_users: number;
}

export interface OnboardingChoiceOption {
    value: string;
    users: number;
    pct: number;
}

export interface OnboardingPathOutcome {
    path: string;
    users: number;
    captured_7d: number;
    pct_captured_7d: number;
    eligible_7d: number;
    ret_d1: number;
    pct_ret_d1: number;
    active_d2_d7: number;
    pct_active_d2_d7: number;
}

export interface OnboardingChoiceStep {
    step_key: string;
    step_label: string;
    users_with_choice: number;
    options: OnboardingChoiceOption[];
}

export interface OnboardingClubChoiceRow {
    club_id: string;
    club_name: string;
    is_official?: boolean;
    users: number;
    users_auto?: number;
    users_manual?: number;
    pct_of_cohort?: number;
    pct_of_joined_events?: number;
}

export interface OnboardingChoices {
    note?: string;
    steps: OnboardingChoiceStep[];
    modalities: OnboardingChoiceStep & {
        users_with_modes_list?: number;
    };
    clubs: {
        users_saw_clubs_step: number;
        users_skipped_extras: number;
        users_joined_any: number;
        by_club: OnboardingClubChoiceRow[];
        extras_selected: OnboardingClubChoiceRow[];
    };
}

export interface OnboardingFunnelAnalytics {
    period_days: number;
    onboarding_v2: {
        welcome_viewed: number;
        level_selected: number;
        water_selected: number;
        clubs_viewed: number;
        clubs_joined: number;
        first_capture_cta_viewed?: number;
        completed_start_capture?: number;
        completed_later?: number;
        /** Path post-retiro de la pantalla CTA (Welcome cierra tras clubes). */
        completed_no_capture_step?: number;
        completed_with_capture?: number;
        completed_without_capture?: number;
        pct_completed_with_capture?: number;
        pct_completed_without_capture?: number;
        /** Outcomes por path: captura ≤7d + retención (eligible ≥7d). */
        path_outcomes?: OnboardingPathOutcome[];
        /** @deprecated legado cromo; puede seguir llegando 0 */
        gift_chosen: number;
        completed: number;
        avg_elapsed_ms: number | null;
    };
    choices?: OnboardingChoices;
    profile_onboarding: {
        card_viewed: number;
        completed: number;
        pro_trial_claimed: number;
        steps: OnboardingProfileStep[];
    };
    permissions: {
        location_intro_shown: number;
        location_result: Array<{ result: string; n: number }>;
        notification_intro_shown: number;
        notification_result: OnboardingPermissionResult[];
    };
    deeplinks: OnboardingDeeplinkRow[];
}

function asChoiceStep(raw: unknown, fallbackLabel: string): OnboardingChoiceStep {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        step_key: String(o.step_key ?? ''),
        step_label: String(o.step_label ?? fallbackLabel),
        users_with_choice: Number(o.users_with_choice ?? 0),
        options: Array.isArray(o.options) ? (o.options as OnboardingChoiceOption[]) : [],
    };
}

function parseOnboarding(raw: unknown): OnboardingFunnelAnalytics {
    let payload: unknown = raw;
    if (Array.isArray(payload)) payload = payload[0];
    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        if (obj.get_onboarding_funnel_analytics) payload = obj.get_onboarding_funnel_analytics;
    }
    const p = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
    const choicesRaw = (p.choices && typeof p.choices === 'object' ? p.choices : {}) as Record<string, unknown>;
    const clubsRaw = (choicesRaw.clubs && typeof choicesRaw.clubs === 'object' ? choicesRaw.clubs : {}) as Record<string, unknown>;
    const modalitiesRaw = choicesRaw.modalities;

    return {
        ...(p as unknown as OnboardingFunnelAnalytics),
        choices: {
            note: typeof choicesRaw.note === 'string' ? choicesRaw.note : undefined,
            steps: Array.isArray(choicesRaw.steps) ? (choicesRaw.steps as OnboardingChoiceStep[]) : [],
            modalities: {
                ...asChoiceStep(modalitiesRaw, 'Modalidades (perfil)'),
                users_with_modes_list: Number(
                    (modalitiesRaw && typeof modalitiesRaw === 'object'
                        ? (modalitiesRaw as Record<string, unknown>).users_with_modes_list
                        : 0) ?? 0
                ),
            },
            clubs: {
                users_saw_clubs_step: Number(clubsRaw.users_saw_clubs_step ?? 0),
                users_skipped_extras: Number(clubsRaw.users_skipped_extras ?? 0),
                users_joined_any: Number(clubsRaw.users_joined_any ?? 0),
                by_club: Array.isArray(clubsRaw.by_club) ? (clubsRaw.by_club as OnboardingClubChoiceRow[]) : [],
                extras_selected: Array.isArray(clubsRaw.extras_selected)
                    ? (clubsRaw.extras_selected as OnboardingClubChoiceRow[])
                    : [],
            },
        },
    };
}

export function useOnboardingAnalytics(days: number = 30) {
    return useQuery<OnboardingFunnelAnalytics>({
        queryKey: ['admin', 'onboarding-analytics', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_onboarding_funnel_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return parseOnboarding(normalized[0] || data);
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

/** Calcula % respecto al paso anterior (0-100). */
export function onboardingStepRate(current: number, previous: number): number {
    if (previous <= 0) return 0;
    return Math.round((current / previous) * 100);
}

export const WATER_CHOICE_LABELS: Record<string, string> = {
    dulce: 'Agua dulce',
    salada: 'Agua salada',
    ambas: 'Ambas',
};

export const LEVEL_CHOICE_LABELS: Record<string, string> = {
    principiante: 'Principiante',
    intermedio: 'Intermedio',
    experto: 'Experto',
};

const FIRST_CAPTURE_PATH_LABELS: Record<string, string> = {
    start_capture: 'Empezar captura (CTA antiguo)',
    later: 'Más adelante (skip CTA)',
    no_capture_step: 'Sin pantalla CTA (actual)',
    unknown: 'Sin path (legado)',
};

export function choiceValueLabel(stepKey: string, value: string): string {
    if (stepKey === 'water') return WATER_CHOICE_LABELS[value] || value;
    if (stepKey === 'level') return LEVEL_CHOICE_LABELS[value] || value;
    if (stepKey === 'first_capture_path') return FIRST_CAPTURE_PATH_LABELS[value] || value;
    return value;
}
